import React, { useState } from "react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Phase 13 — Compact email-capture form attached to Under-Construction badges
 * on the public /pricing page. Idempotent: backend handles duplicates gracefully.
 *
 * Props:
 *   featureKey: one of "payroll_live" | "ap_live" | "ar_live" | "pos_live" | "corporate"
 *   planId:    optional context "business" | "enterprise" | "corporate"
 *   compact:   render inline (single line); else stacked
 *   dark:      use dark-mode styles (default true since /pricing is dark-themed)
 */
export default function NotifyMeForm({ featureKey, planId = null, compact = true, dark = true, dataTestId }) {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null); // 'success' | 'duplicate' | 'error'
  const [message, setMessage] = useState("");

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!email || submitting) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/api/waitlist`, {
        email: email.trim().toLowerCase(),
        feature_key: featureKey,
        plan_id: planId,
        source: "pricing_page",
      });
      setStatus(res.data.created ? "success" : "duplicate");
      setMessage(res.data.message || "You're on the list!");
      if (res.data.created) setEmail("");
    } catch (err) {
      setStatus("error");
      const detail = err.response?.data?.detail;
      setMessage(typeof detail === "string" ? detail : "Could not save — please retry.");
    } finally {
      setSubmitting(false);
    }
  };

  if (status === "success" || status === "duplicate") {
    return (
      <p
        className={`text-xs mt-1 ${status === "success" ? "text-emerald-400" : "text-amber-300"}`}
        data-testid={`${dataTestId || "notify-me"}-success`}
      >
        {message}
      </p>
    );
  }

  const inputCls = dark
    ? "bg-slate-900/70 border border-slate-700 text-slate-100 placeholder-slate-500"
    : "bg-white border border-slate-200 text-slate-900 placeholder-slate-400";

  return (
    <form
      onSubmit={submit}
      className={`flex ${compact ? "items-center gap-1.5" : "flex-col gap-2"} mt-1`}
      data-testid={dataTestId || "notify-me-form"}
    >
      <input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Email me when this launches"
        className={`flex-1 min-w-0 rounded px-2 py-1 text-xs ${inputCls} focus:outline-none focus:ring-1 focus:ring-cyan-400`}
        data-testid={`${dataTestId || "notify-me"}-input`}
        disabled={submitting}
      />
      <button
        type="submit"
        disabled={submitting || !email}
        className="rounded px-2.5 py-1 text-xs font-medium bg-cyan-500 hover:bg-cyan-600 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        data-testid={`${dataTestId || "notify-me"}-submit`}
      >
        {submitting ? "..." : "Notify"}
      </button>
      {status === "error" && (
        <p className="text-[10px] text-red-400 mt-0.5" data-testid={`${dataTestId || "notify-me"}-error`}>
          {message}
        </p>
      )}
    </form>
  );
}
